import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Package } from "lucide-react";

const empty = {
  nombre: "", categoria: "Soporte Flexible", unidad: "m²",
  precio_coste: 0, precio_venta: 0, proveedor_id: "", notas: "",
  formato_suministro: "Bobina", ancho_bobina_cm: "", largo_bobina_m: "", merma_pct: "",
  ancho_imprimible_cm: "", perdida_corte_cm: 20,
  controla_stock: false, stock_actual: 0, stock_minimo: 0, ubicacion_almacen: "",
};

const CATEGORIAS = ["Soporte Flexible", "Soporte Rígido", "Tintas", "Herrajes / Consumibles", "Otro"];
const FORMATOS = ["Bobina", "Planchas", "Unidades", "ml", "Kg", "litros"];

export default function MaterialesPage() {
  const [items, setItems] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const load = () => Promise.all([api.get("/materiales"), api.get("/proveedores")])
    .then(([m, p]) => { setItems(m.data); setProveedores(p.data); });
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      precio_coste: parseFloat(form.precio_coste) || 0,
      precio_venta: parseFloat(form.precio_venta) || 0,
      ancho_bobina_cm: form.ancho_bobina_cm !== "" ? parseFloat(form.ancho_bobina_cm) : null,
      largo_bobina_m: form.largo_bobina_m !== "" ? parseFloat(form.largo_bobina_m) : null,
      merma_pct: form.merma_pct !== "" ? parseFloat(form.merma_pct) : null,
      ancho_imprimible_cm: form.ancho_imprimible_cm !== "" ? parseFloat(form.ancho_imprimible_cm) : null,
      perdida_corte_cm: form.perdida_corte_cm !== "" ? parseFloat(form.perdida_corte_cm) : 20,
      controla_stock: !!form.controla_stock,
      stock_actual: form.stock_actual !== "" ? parseFloat(form.stock_actual) || 0 : 0,
      stock_minimo: form.stock_minimo !== "" ? parseFloat(form.stock_minimo) || 0 : 0,
      ubicacion_almacen: form.ubicacion_almacen || null,
    };
    Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
    if (editing) await api.patch(`/materiales/${editing.id}`, payload);
    else await api.post("/materiales", payload);
    setOpen(false); load();
  };

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Materiales y tarifas"
        subtitle="Catálogo de materias primas con precio coste y venta"
        action={
          <Button onClick={openCreate} data-testid="new-material-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Nuevo material
          </Button>
        }
      />

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_0.7fr_1fr_1fr_1fr_0.8fr] gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-zinc-500 border-b border-zinc-800 bg-zinc-950/50">
          <div>Material</div><div>Categoría</div><div>Unidad</div>
          <div>Coste</div><div>Venta</div><div>Margen</div><div></div>
        </div>
        {items.map((m) => {
          const prov = proveedores.find((p) => p.id === m.proveedor_id);
          const margen = m.precio_venta > 0 ? (((m.precio_venta - m.precio_coste) / m.precio_venta) * 100).toFixed(0) : 0;
          return (
            <div key={m.id} data-testid={`material-row-${m.id}`}
              className="grid grid-cols-[2fr_1fr_0.7fr_1fr_1fr_1fr_0.8fr] gap-3 px-4 py-3 items-center border-b border-zinc-800/60 last:border-b-0 hover:bg-zinc-900/40">
              <div>
                <div className="text-sm font-medium text-zinc-100 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 text-amber-400" /> {m.nombre}
                </div>
                {prov && <div className="text-[11px] text-zinc-500 ml-5">via {prov.nombre}</div>}
              </div>
              <div className="text-xs text-zinc-300">
                {m.categoria || "—"}
                {m.formato_suministro && (
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    {m.formato_suministro}
                    {m.ancho_bobina_cm ? ` · ${m.ancho_bobina_cm}cm` : ""}
                    {m.largo_bobina_m ? ` × ${m.largo_bobina_m}m` : ""}
                    {m.merma_pct ? ` · merma ${m.merma_pct}%` : ""}
                  </div>
                )}
              </div>
              <div className="text-xs text-zinc-300">{m.unidad}</div>
              <div className="text-xs text-zinc-300">
                {(m.precio_coste || 0).toFixed(2)} €
                {m.controla_stock && (
                  <div className={`text-[10px] mt-0.5 ${(m.stock_actual || 0) < (m.stock_minimo || 0) ? "text-rose-400 font-semibold" : "text-emerald-400"}`}>
                    stock {(m.stock_actual || 0).toFixed(1)} {m.unidad}
                  </div>
                )}
              </div>
              <div className="text-xs text-amber-300 font-medium">{(m.precio_venta || 0).toFixed(2)} €</div>
              <div className="text-xs text-emerald-300">{margen}%</div>
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => openEdit(m)} className="h-7 w-7 p-0 text-zinc-400 hover:text-amber-300">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost"
                  onClick={async () => { if (confirm(`¿Eliminar ${m.nombre}?`)) { await api.delete(`/materiales/${m.id}`); load(); } }}
                  className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="p-12 text-center text-zinc-500 text-sm">Sin materiales todavía.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar material" : "Nuevo material"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nombre *</Label>
                <Input required value={form.nombre} data-testid="form-material-nombre"
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Categoría</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {CATEGORIAS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Unidad</Label>
                <Select value={form.unidad} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {["m²", "ud", "ml", "kg", "litros", "hora"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Precio coste (€)</Label>
                <Input type="number" step="0.01" value={form.precio_coste}
                  onChange={(e) => setForm({ ...form, precio_coste: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Precio venta (€)</Label>
                <Input type="number" step="0.01" value={form.precio_venta}
                  onChange={(e) => setForm({ ...form, precio_venta: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>

              {/* === SUMINISTRO === */}
              <div className="col-span-2 mt-2 pt-2 border-t border-zinc-800">
                <div className="text-[10px] uppercase tracking-[0.16em] text-amber-400/80 mb-2">Datos de suministro</div>
              </div>
              <div><Label>Formato suministro</Label>
                <Select value={form.formato_suministro} onValueChange={(v) => setForm({ ...form, formato_suministro: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {FORMATOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Merma (%)</Label>
                <Input type="number" step="0.1" placeholder="ej. 10" value={form.merma_pct}
                  onChange={(e) => setForm({ ...form, merma_pct: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" /></div>
              {form.formato_suministro === "Bobina" && (
                <>
                  <div><Label>Ancho bobina (cm)</Label>
                    <Input type="number" step="0.1" value={form.ancho_bobina_cm}
                      onChange={(e) => setForm({ ...form, ancho_bobina_cm: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" /></div>
                  <div><Label>Largo bobina (m)</Label>
                    <Input type="number" step="0.1" value={form.largo_bobina_m}
                      onChange={(e) => setForm({ ...form, largo_bobina_m: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" /></div>
                  <div><Label>Ancho imprimible (cm) <span className="text-zinc-500 text-[10px]">útil de la máquina</span></Label>
                    <Input type="number" step="0.1" placeholder="ej. 137" value={form.ancho_imprimible_cm}
                      onChange={(e) => setForm({ ...form, ancho_imprimible_cm: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" /></div>
                  <div><Label>Pérdida corte (cm) <span className="text-zinc-500 text-[10px]">cabecera+pie</span></Label>
                    <Input type="number" step="0.1" placeholder="20" value={form.perdida_corte_cm}
                      onChange={(e) => setForm({ ...form, perdida_corte_cm: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" /></div>
                </>
              )}

              <div className="col-span-2"><Label>Proveedor</Label>
                <Select value={form.proveedor_id} onValueChange={(v) => setForm({ ...form, proveedor_id: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {proveedores.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="col-span-2"><Label>Notas</Label>
                <Textarea value={form.notas} rows={2} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>

              {/* === ALMACÉN / STOCK === */}
              <div className="col-span-2 mt-2 pt-2 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-amber-400/80">Control de stock</div>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={!!form.controla_stock}
                           onChange={(e) => setForm({ ...form, controla_stock: e.target.checked })}
                           data-testid="material-controla-stock"
                           className="w-4 h-4 accent-amber-500" />
                    <span className="text-zinc-200">Llevar inventario de este material</span>
                  </label>
                </div>
              </div>
              {form.controla_stock && (
                <>
                  <div><Label>Stock actual ({form.unidad})</Label>
                    <Input type="number" step="0.01" value={form.stock_actual}
                      onChange={(e) => setForm({ ...form, stock_actual: e.target.value })}
                      data-testid="material-stock-actual"
                      className="bg-zinc-900 border-zinc-800" /></div>
                  <div><Label>Stock mínimo (alerta)</Label>
                    <Input type="number" step="0.01" value={form.stock_minimo}
                      onChange={(e) => setForm({ ...form, stock_minimo: e.target.value })}
                      data-testid="material-stock-minimo"
                      className="bg-zinc-900 border-zinc-800" /></div>
                  <div className="col-span-2"><Label>Ubicación en almacén</Label>
                    <Input value={form.ubicacion_almacen}
                      onChange={(e) => setForm({ ...form, ubicacion_almacen: e.target.value })}
                      placeholder="Ej. Estantería A3, balda 2"
                      className="bg-zinc-900 border-zinc-800" /></div>
                  <div className="col-span-2 text-[11px] text-zinc-500">
                    Las salidas se generan automáticamente al pasar componentes a estado «en producción» (m² × cantidad). Movimientos manuales en <b className="text-amber-400">Almacén</b>.
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
              <Button type="submit" data-testid="form-material-submit" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
